import { PropsWithChildren, ReactNode, useState } from "react";
import { IconButton,
  Box, type BoxProps,
  DrawerBody,
  Flex,
  Icon,
  List,
  Text,
  Badge,
  Link as ChakraLink,
  Heading,
  Image,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MdArrowBackIos,
  MdMenu,
  MdLogout,
  MdDownload,
  MdKey,
  MdNumbers,
} from "react-icons/md";
import {
  DrawerRoot,
  DrawerBackdrop,
  DrawerTrigger,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "../ui/drawer";
import { Button } from "../ui/button";
import { CloseButton } from "../ui/close-button";
import { useAuth } from "@/hooks/useAuth";
import { useCacheData } from "@/hooks";
import packageJson from "../../../package.json";

interface PageHeader extends PropsWithChildren {
  backLink?: string | number;
  children: ReactNode;
}

function PageHeader({ children, backLink, ...props }: PageHeader & BoxProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [showDownloadSuccess, setShowDownloadSuccess] =
    useState<boolean>(false);
  const [showDownloadError, setShowDownloadError] = useState<boolean>(false);

  const { isAuthenticated, logout } = useAuth();
  const { fetchUserData, isPending, error } = useCacheData();
  const showMenu = isAuthenticated; // This condition will likely change or will be removed altogether in the future.
  const cacheDate = localStorage.getItem("cache_date");

  const handleDownload = () => {
    setShowDownloadError(false);
    fetchUserData()
      .then(() => {
        setDrawerOpen(false);
        setShowDownloadSuccess(true);
        setTimeout(() => setShowDownloadSuccess(false), 2000);
      })
      .catch(() => {
        setDrawerOpen(false);
        setShowDownloadError(true);
      });
  };

  const handleLogout = () => {
    logout();
    setDrawerOpen(false);
  };

  return (
    <>
      {showDownloadError && error && (
        <Flex
          gap="4"
          alignItems="center"
          p="4"
          bgColor="red.100"
          color="red.700"
          fontWeight="medium"
        >
          <Text flex="1">
            Unable to download nursery data. Please try again.
          </Text>
          <CloseButton
            variant="ghost"
            colorPalette="red"
            onClick={() => setShowDownloadError(false)}
          />
        </Flex>
      )}
      {showDownloadSuccess && (
        <Box p="4" bgColor="green.100" color="green.700" fontWeight="medium">
          Nursery data ready for offline use
        </Box>
      )}
      <Box
        bgColor={{base: "blue.700", sm: "bg"}}
        color={{ base: "white", sm:"gray.700"}}
        borderBottomWidth="1px"
        py="2"
        {...props}
      >
        <Flex gap="2" alignItems="center" justifyContent="space-between" maxW={{ md: "7xl", "2xl": "8xl"}} mx="auto" px="4">
          {backLink && (
            <Box hideFrom="sm">
              {typeof backLink === "string"
                ? <Link to={backLink} aria-label="Back to previous page"><MdArrowBackIos /></Link>
                : (
                  <Button
                    color={{ base: "white", sm: "gray.700" }}
                    bgColor={{base: "transparent", sm: "bg"}}
                    onClick={() => navigate(-1)}
                    aria-label="Back to previous page"
                  >
                    <MdArrowBackIos />
                  </Button>
                )}
            </Box>
          )}
          <Link to="/">
            <Flex hideBelow="sm" alignItems="center" gap="2" role="group" className="group">
              <Box role="group" display="flex" alignItems="center" gap="2">
                <Image
                  src="/logo.svg"
                  alt="Coracle Logo"
                  height="36px"
                  transition="all 0.64s ease"
                  _groupHover={{ opacity: 0.64 }}
                />
                <Box>
                  <Heading transition="all 0.64s ease" _groupHover={{ opacity: 0.64 }} fontWeight="bold" size="2xl" color="gray.900" lineHeight="1" letterSpacing="-0.5px">Coracle</Heading>
                  <Heading size="xs" as="p" color="gray.400" textTransform="uppercase" lineHeight="1" letterSpacing="0.5px" fontWeight="semibold">Coral Growth Monitoring</Heading>
                </Box>
              </Box>
            </Flex>
          </Link>
          <Text hideFrom="sm" as="h1" fontWeight="500" textAlign="center" fontSize="sm" flex="1">
            {children}
          </Text>
          {isAuthenticated && !location.pathname.endsWith("/observation/") && (
            <Box ml="auto" mr={3}>
              <Link to="/count">
                <Button colorPalette={{base:"white", sm:"blue"}} color={{ base: "white", sm: "blue.500" }} variant="outline" size="xs" textTransform="uppercase">
                  <MdNumbers />Counter
                </Button>
              </Link>
            </Box>
          )}

          {showMenu && (
            <DrawerRoot
              open={drawerOpen}
              onOpenChange={(e) => setDrawerOpen(e.open)}
            >
              <DrawerBackdrop />
              <DrawerTrigger asChild>
                <IconButton variant="outline" size="sm" aria-label="Open Menu" color={{ base: "white", sm: "currentColor" }}>
                  <MdMenu />
                </IconButton>
              </DrawerTrigger>
              <DrawerContent>
                <DrawerHeader>
                  <Box>
                    <Heading fontWeight="bold" size="2xl" color="gray.700" lineHeight="1.1">Coracle</Heading>
                    <Heading size="xs" as="p" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" fontWeight="bold">Coral Growth Monitoring</Heading>
                  </Box>
                </DrawerHeader>
                <DrawerBody pt="12">
                  <List.Root
                    variant="plain"
                    css={{
                      "& > * > *:first-child": {
                        flex: 1,
                        justifyContent: "flex-start",
                      },
                    }}
                  >
                    {isAuthenticated && (
                      <>
                        <List.Item>
                          <Button
                            variant="ghost"
                            onClick={handleDownload}
                            loading={isPending}
                            loadingText="Downloading..."
                            disabled={isPending}
                          >
                            <Icon fontSize="md">
                              <MdDownload />
                            </Icon>
                            Download data for offline use
                          </Button>
                        </List.Item>
                        <List.Item>
                          <Button variant="ghost" asChild>
                            <Link to="/user/password">
                              <Icon fontSize="md">
                                <MdKey />
                              </Icon>
                              Change password
                            </Link>
                          </Button>
                        </List.Item>
                        <List.Item>
                          <Button variant="ghost" onClick={handleLogout}>
                            <Icon fontSize="md">
                              <MdLogout />
                            </Icon>
                            Logout
                          </Button>
                        </List.Item>
                      </>
                    )}
                  </List.Root>
                </DrawerBody>
                <DrawerFooter
                  display="flex"
                  flexDir="column"
                  gap="1"
                  alignItems="start"
                  px="6"
                  fontSize="xs"
                  color="gray.500"
                >
                  {cacheDate && (
                    <Text fontSize="xs" color="gray.500">
                      Last download:{" "}
                      {new Date(cacheDate).toLocaleString("en-US", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </Text>
                  )}
                  <Heading width="100%" size="xs" flexBasis="100%" fontWeight="semibold">Model Versions</Heading>
                  <Flex gap={1} mb={2}>
                    <Text fontSize="xs" color="gray.500" display="inline-flex" gap={1}>
                      Object{" "}
                      <Badge colorPalette="blue" variant="subtle">
                        v0.1.0
                      </Badge>
                    </Text>
                    <Text fontSize="xs" color="gray.500" display="inline-flex" gap={1}>
                      Health{" "}
                      <Badge colorPalette="blue" variant="subtle">
                        v0.1.0
                      </Badge>
                    </Text>
                    <Text fontSize="xs" color="gray.500" display="inline-flex" gap={1}>
                      Area{" "}
                      <Badge colorPalette="blue" variant="subtle">
                        v0.1.0
                      </Badge>
                    </Text>
                  </Flex>
                  <Text fontSize="xs" color="gray.500">
                    ©{new Date().getFullYear()} The Nature Conservancy
                  </Text>
                  {packageJson.version && (
                    <Badge colorPalette="blue" variant="subtle">
                      <ChakraLink
                        href={`https://github.com/developmentseed/tnc-coral-pwa/releases/tag/v${packageJson.version}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                      >
                        v{packageJson.version}
                      </ChakraLink>
                    </Badge>
                  )}
                </DrawerFooter>
                <DrawerCloseTrigger />
              </DrawerContent>
            </DrawerRoot>
          )}
        </Flex>
      </Box>
    </>
  );
}

export default PageHeader;
